import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { ChipGroup } from "./fields";
import { X } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, value, onChange, testId, rows = 2 }) => (
  <div>
    <label className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500">{label}</label>
    <textarea data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
  </div>
);

export default function EditStudentSheet({ student, onClose, onSaved }) {
  const [promptLevels, setPromptLevels] = useState([]);
  const [snap, setSnap] = useState({
    what_helps: student.snapshot.what_helps || "",
    triggers: student.snapshot.triggers || "",
    how_to_redirect: student.snapshot.how_to_redirect || "",
    expected_prompting: student.snapshot.expected_prompting || "Verbal",
    accommodations: (student.snapshot.accommodations || []).join(", "),
    what_not_to_do: student.snapshot.what_not_to_do || "",
    contact_on_issue: student.snapshot.contact_on_issue || "",
    weekly_priorities: student.snapshot.weekly_priorities || "",
  });
  const [prof, setProf] = useState({
    support_areas: (student.profile.support_areas || []).join(", "),
    behavior_strategies: student.profile.behavior_strategies || "",
    accommodations: (student.profile.accommodations || []).join(", "),
    prompting_guidance: student.profile.prompting_guidance || "",
    safety_protocols: student.profile.safety_protocols || "",
    communication_prefs: student.profile.communication_prefs || "",
  });
  const [saving, setSaving] = useState(false);
  const setS = (k) => (v) => setSnap((f) => ({ ...f, [k]: v }));
  const setP = (k) => (v) => setProf((f) => ({ ...f, [k]: v }));
  const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  useEffect(() => { api.get("/meta/taxonomies").then((r) => setPromptLevels(r.data.prompt_levels)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/students/${student.id}/snapshot`, { ...snap, accommodations: toList(snap.accommodations) });
      await api.put(`/students/${student.id}/profile`, { ...prof, support_areas: toList(prof.support_areas), accommodations: toList(prof.accommodations) });
      toast.success("Saved — Snapshot version bumped; aides must re-acknowledge");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 sf-overlay flex items-end sm:items-center justify-center" onClick={onClose}>
      <div data-testid="edit-student-sheet" className="sf-sheet bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-heading font-semibold text-slate-900">Edit Snapshot & Profile</h2>
            <p className="text-xs text-slate-400">{student.first_name} {student.last_name} · saving bumps Snapshot to v{student.snapshot.version + 1}</p>
          </div>
          <button data-testid="close-edit-sheet-btn" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-emerald-700">Support Snapshot</p>
          <Field label="What helps this student succeed" value={snap.what_helps} onChange={setS("what_helps")} testId="edit-what-helps" />
          <Field label="Common triggers for dysregulation" value={snap.triggers} onChange={setS("triggers")} testId="edit-triggers" />
          <Field label="How to redirect" value={snap.how_to_redirect} onChange={setS("how_to_redirect")} testId="edit-redirect" />
          <ChipGroup label="Expected prompting level" options={promptLevels} value={snap.expected_prompting} onChange={setS("expected_prompting")} testPrefix="edit-prompting" />
          <Field label="Required accommodations (comma-separated)" value={snap.accommodations} onChange={setS("accommodations")} testId="edit-snap-accommodations" rows={1} />
          <Field label="What NOT to do" value={snap.what_not_to_do} onChange={setS("what_not_to_do")} testId="edit-not-to-do" />
          <Field label="Who to contact when an issue occurs" value={snap.contact_on_issue} onChange={setS("contact_on_issue")} testId="edit-contact" rows={1} />
          <Field label="Current weekly priorities" value={snap.weekly_priorities} onChange={setS("weekly_priorities")} testId="edit-priorities" />

          <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 pt-2 border-t border-slate-100">Support Profile</p>
          <Field label="Support areas (comma-separated)" value={prof.support_areas} onChange={setP("support_areas")} testId="edit-support-areas" rows={1} />
          <Field label="Behavior support strategies" value={prof.behavior_strategies} onChange={setP("behavior_strategies")} testId="edit-strategies" />
          <Field label="Accommodations picklist (comma-separated — used in block entries)" value={prof.accommodations} onChange={setP("accommodations")} testId="edit-prof-accommodations" rows={1} />
          <Field label="Prompting guidance" value={prof.prompting_guidance} onChange={setP("prompting_guidance")} testId="edit-guidance" />
          <Field label="Safety / elopement protocols" value={prof.safety_protocols} onChange={setP("safety_protocols")} testId="edit-safety" />
          <Field label="Communication preferences" value={prof.communication_prefs} onChange={setP("communication_prefs")} testId="edit-comm-prefs" />
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4">
          <button data-testid="save-edit-student-btn" onClick={save} disabled={saving}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-full py-3 text-sm transition-colors duration-150 active:scale-[0.98] disabled:opacity-60">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
